import { graphqlSchema } from "app/graphqlSchema";
import {
  getNamedType,
  getNullableType,
  GraphQLField,
  GraphQLObjectType,
  GraphQLOutputType,
  isListType,
} from "graphql";
import { getGraphQLTypeMetadata } from "./getGraphQLTypeMetadata";

function lowerFirst(value: string) {
  return value.charAt(0).toLowerCase() + value.slice(1);
}

function getPluralFieldNameCandidates(typeName: string) {
  const fieldNameBase = lowerFirst(typeName);

  if (typeName === "CustomData") {
    return ["customData"];
  }

  if (fieldNameBase.match(/[^aeiou]y$/)) {
    return [`${fieldNameBase.slice(0, -1)}ies`, `${fieldNameBase}s`];
  }

  if (fieldNameBase.match(/(s|x|z|ch|sh)$/)) {
    return [`${fieldNameBase}es`, `${fieldNameBase}s`];
  }

  return [`${fieldNameBase}s`];
}

function isListOutputType(type: GraphQLOutputType): boolean {
  const nullableType = getNullableType(type);

  if (isListType(nullableType)) {
    return true;
  }

  return false;
}

function getDefaultQueryFieldByReturnType(
  recordType: GraphQLObjectType,
  fields: Record<string, GraphQLField<any, any>>,
) {
  return Object.values(fields).find(
    (field) =>
      isListOutputType(field.type) &&
      getNamedType(field.type).name === recordType.name &&
      field.args.some((arg) => arg.name === "filter"),
  );
}

/**
 * Takes a record type like "Job" and returns the default query field, eg "jobs"
 */
export function getDefaultQueryFieldForType(
  recordType: GraphQLObjectType,
): GraphQLField<any, any> {
  const queryType = graphqlSchema.getQueryType();
  if (!queryType) {
    throw new Error("Schema does not have a Query type");
  }

  const fields = queryType.getFields();
  const metadataDefaultQuery = getGraphQLTypeMetadata(recordType)?.defaultQuery;
  if (metadataDefaultQuery) {
    const defaultQueryField = fields[metadataDefaultQuery];
    if (!defaultQueryField) {
      throw new Error(
        `Metadata defaultQuery "${metadataDefaultQuery}" for type "${recordType.name}" does not exist on Query`,
      );
    }

    return defaultQueryField;
  }

  let maybeField: GraphQLField<any, any> | undefined;

  for (const fieldName of getPluralFieldNameCandidates(recordType.name)) {
    maybeField = fields[fieldName];
    if (maybeField) {
      break;
    }
  }

  maybeField ??= getDefaultQueryFieldByReturnType(recordType, fields);

  if (!maybeField) {
    throw new Error(
      `Could not find default query field for type "${recordType.name}"`,
    );
  }
  return maybeField;
}
